import type { ReactNode } from "react";
import { Search } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import type { GeneratorUILabels } from "@/types/generator";
import type { MatchedStandard } from "@/types/standards";
import { DEFAULT_UI_LABELS } from "@/types/generator";
import { getDisplayableStandardLabels } from "@/lib/standard-references";

export interface CarouselSubItem {
  id: string;
  label: string;
  status: "searching" | "found" | "none" | "removing";
  count?: number;
}

export interface ProcessingCarouselsProps {
  detectedCategories: string[];
  showCategories: boolean;
  subItems: CarouselSubItem[];
  matchedStandards?: MatchedStandard[];
  isSearching: boolean;
  isSearchComplete: boolean;
  jurisdiction?: string;
  isAnalyzingHazards?: boolean;
  totalSourcesCount?: number;
  uiLabels?: GeneratorUILabels;
  showStandardsSection?: boolean;
  showAnalysisSection?: boolean;
  /** Optional slot rendered above analysis/standards (e.g. AttachmentAnalysisRows). */
  attachmentSection?: ReactNode;
  /** Optional slot rendered between attachments and standards (e.g. CorrelationSummaryRow). */
  correlationSection?: ReactNode;
}

// Format category names: working_at_heights -> "Working at Heights"
const formatCategoryLabel = (category: string): string => {
  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export function ProcessingCarousels({
  detectedCategories,
  showCategories,
  subItems,
  matchedStandards = [],
  isSearching,
  isSearchComplete,
  jurisdiction,
  isAnalyzingHazards,
  totalSourcesCount = 0,
  uiLabels = DEFAULT_UI_LABELS,
  showStandardsSection = true,
  showAnalysisSection = true,
  attachmentSection,
  correlationSection,
}: ProcessingCarouselsProps) {
  // Use database-driven labels with defaults
  const analysisHeading = uiLabels.analysis_heading || DEFAULT_UI_LABELS.analysis_heading;
  const scanningLabel = uiLabels.scanning_label || DEFAULT_UI_LABELS.scanning_label;
  const scanningMessage = uiLabels.scanning_message || DEFAULT_UI_LABELS.scanning_message;
  const identifiedLabel = uiLabels.identified_label || DEFAULT_UI_LABELS.identified_label;
  const noneDetectedMessage = uiLabels.none_detected_message || DEFAULT_UI_LABELS.none_detected_message;
  const standardsSectionHeading = uiLabels.standards_section_heading || DEFAULT_UI_LABELS.standards_section_heading;
  const standardsCheckingMessage = uiLabels.standards_checking_message || DEFAULT_UI_LABELS.standards_checking_message;
  const standardsPreparingMessage = uiLabels.standards_preparing_message || DEFAULT_UI_LABELS.standards_preparing_message;
  const foundSourceLabels = new Set(
    subItems.filter((item) => item.status === "found").map((item) => item.label),
  );
  const visibleMatchedStandards = matchedStandards.filter((standard) =>
    foundSourceLabels.has(standard.source_document),
  );
  const matchedItems = getDisplayableStandardLabels(visibleMatchedStandards).map((label) => ({
    id: label,
    label,
  }));
  const scanningItems = subItems.filter(item => item.status === "searching" || item.status === "removing");

  // Progress calculation using original total for consistency
  const originalTotal = totalSourcesCount || subItems.length;
  const currentProcessed = subItems.filter(item => item.status !== "searching").length;
  const removedCount = originalTotal - subItems.length;
  const totalProcessed = currentProcessed + removedCount;
  const progressPercent = originalTotal > 0 ? (totalProcessed / originalTotal) * 100 : 0;

  return (
    <div className="flex h-full min-w-0 flex-col overflow-hidden overflow-y-auto max-h-[70vh]">
      {/* Slot: Attachment analysis (rendered above analysis when present) */}
      {attachmentSection}

      {/* Slot: Correlation summary (rendered between attachments and standards) */}
      {correlationSection}

      {/* Section 1: Analysis (Hazard for SWMS, Requirements for ITP) - Only show if activity analysis is enabled */}
      {showAnalysisSection && (
        <div className="mb-6 min-w-0">
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {analysisHeading}
          </h4>
          <div className="min-h-[40px] min-w-0">
            {/* Scanning state */}
            {!showCategories && isAnalyzingHazards && (
              <div className="mb-2 space-y-1.5 min-w-0">
                <div className="flex min-w-0 items-center gap-2 overflow-hidden">
                  <Search className="h-4 w-4 text-primary animate-pulse flex-shrink-0" />
                  <span className="block min-w-0 truncate text-sm text-muted-foreground">
                    {scanningMessage}
                  </span>
                </div>
              </div>
            )}
            
            {/* Waiting state before scanning starts */}
            {!showCategories && !isAnalyzingHazards && (
              <div className="flex flex-wrap gap-1.5 min-w-0">
                <Badge variant="outline" className="text-xs text-muted-foreground animate-pulse max-w-full">
                  <span className="block min-w-0 truncate">{scanningLabel}</span>
                </Badge>
              </div>
            )}
            
            {/* Show detected items when ready */}
            {showCategories && detectedCategories.length > 0 && (
              <div className="min-w-0">
                <p className="mb-2 text-sm font-medium text-primary animate-fade-in">
                  {identifiedLabel} Identified ({detectedCategories.length})
                </p>
                <div className="flex min-w-0 flex-wrap gap-1.5 overflow-hidden">
                  {detectedCategories.map((category, index) => (
                    <Badge 
                      key={category} 
                      variant="default"
                      className="max-w-full text-xs font-medium opacity-0 animate-fade-in"
                      style={{ 
                        animationDelay: `${300 + (index * 150)}ms`,
                        animationFillMode: 'forwards'
                      }}
                    >
                      <span className="block min-w-0 truncate">{formatCategoryLabel(category)}</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* No items detected */}
            {showCategories && detectedCategories.length === 0 && (
              <div className="flex flex-wrap gap-1.5 min-w-0">
                <Badge variant="outline" className="text-xs text-muted-foreground max-w-full">
                  <span className="block min-w-0 truncate">{noneDetectedMessage}</span>
                </Badge>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section 2: Standards Analysis (configurable heading) - Only show if required */}
      {showStandardsSection && (
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {standardsSectionHeading}
            {jurisdiction && (
              <span className="ml-2 text-xs font-normal normal-case text-muted-foreground/80">
                ({jurisdiction})
              </span>
            )}
          </h4>
          
          <div className="flex-1 min-h-[80px] min-w-0 overflow-hidden">
            {isSearching && scanningItems.length > 0 && (
              <div className="mb-4 min-w-0 space-y-4 animate-fade-in overflow-hidden">
                <div className="space-y-2 min-w-0 overflow-hidden">
                  <p className="truncate text-sm text-muted-foreground">
                    {standardsCheckingMessage}
                  </p>
                  <div className="space-y-2 min-w-0 overflow-hidden">
                    {scanningItems.slice(0, 2).map((item, index) => (
                      <div
                        key={item.id}
                        className="flex min-w-0 items-center gap-2 overflow-hidden text-sm text-muted-foreground opacity-0 animate-fade-in"
                        style={{
                          animationDelay: `${index * 120}ms`,
                          animationFillMode: 'forwards'
                        }}
                      >
                        <Search className="h-4 w-4 text-primary animate-pulse flex-shrink-0" />
                        <span className="block min-w-0 flex-1 truncate">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex min-w-0 items-center gap-3 overflow-hidden">
                  <Progress value={progressPercent} className="min-w-0 flex-1" />
                  <span className="flex-shrink-0 text-sm text-muted-foreground tabular-nums">
                    {totalProcessed}/{originalTotal}
                  </span>
                </div>
              </div>
            )}

            {isSearching && scanningItems.length === 0 && matchedItems.length === 0 && (
              <p className="mb-4 truncate text-sm text-muted-foreground animate-pulse">
                {standardsPreparingMessage}
              </p>
            )}

            {matchedItems.length > 0 && (
              <div className="min-w-0">
                <p className="mb-2 text-sm font-medium text-primary">
                  Identified ({matchedItems.length})
                </p>
                <div className="flex flex-wrap gap-x-2 gap-y-2">
                  {matchedItems.map((item) => (
                    <Badge
                      key={item.id}
                      variant="default"
                      className="text-xs font-medium animate-fade-in whitespace-normal break-words text-left leading-snug py-1"
                      title={item.label}
                    >
                      {item.label}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
